<template>
   <QBtn id="date-picker" color="primary" :outline="datePickerOpened" icon-right="mdi-calendar">
      <span style="margin-right: 15px">{{ dateRangeStringToDisplay }}</span>
      <QPopupProxy
         v-model="popUpOpen"
         @before-show="datePickerOpened = true"
         @before-hide="datePickerOpened = false"
      >
         <QDate v-model="dateRange" range :options="dateOptions">
            <div class="row items-center justify-end q-gutter-sm">
               <DatePickerShortcuts
                  @date-shortcut-clicked="closePopupDatepicker"
               />
               <QBtn
                  v-close-popup
                  label="Cancel"
                  color="primary"
                  flat
                  @click="closePopupDatepicker"
               />
               <QBtn
                  v-close-popup
                  label="OK"
                  color="primary"
                  flat
                  @click="
                     () => {
                        saveDateRange()
                        closePopupDatepicker()
                     }
                  "
               />
            </div>
         </QDate>
      </QPopupProxy>
   </QBtn>
</template>
<script setup lang="ts">
import { QDate, QPopupProxy, QBtn } from "quasar"
import { computed, ref, toRefs, watch } from "vue"
import DatePickerShortcuts from "./DatePickerShortcuts.vue"
import { useAnalyticsStore } from "@stores/analyticsStore"
import { getTodayAtMidnightUTC, getDateOneYearAgo, fmtDateForDatePickerValue, fmtDateForDatePickerDisplay } from "@/utils/datetimes"



const analyticsStore = useAnalyticsStore()
const { start_date, end_date } = toRefs(analyticsStore)

const dateOptions = (dateString: string) => {
   const dateToCheck = new Date(dateString)

   // Set up lower bound (1 year ago)
   const startDate = getDateOneYearAgo()

   // Set up upper bound (today at 12am UTC)
   const endDate = getTodayAtMidnightUTC()

   // Date must be between startDate (inclusive) and endDate (inclusive)
   return dateToCheck >= startDate && dateToCheck <= endDate
}

const popUpOpen = ref(false)
const datePickerOpened = ref(false)

const formattedStartDate = computed(() => {
   return fmtDateForDatePickerValue(start_date.value)
})

const formattedEndDate = computed(() => {
   return fmtDateForDatePickerValue(end_date.value)
})

const dateRange = ref({
   from: formattedStartDate.value,
   to: formattedEndDate.value,
})

// Keep dateRange in sync with analytics store state
watch(
   () => [start_date.value, end_date.value],
   ([newStartDate, newEndDate]) => {
      dateRange.value = {
         from: fmtDateForDatePickerValue(newStartDate),
         to: fmtDateForDatePickerValue(newEndDate),
      }
   },
)

const dateRangeString = computed(() => {
   const { from, to } = dateRange.value || {}
   return from && to
      ? `${fmtDateForDatePickerDisplay(from)} to ${fmtDateForDatePickerDisplay(to)}`
      : null
})

const dateRangeStringToDisplay = ref(dateRangeString.value)

const updateDateRangeString = () => {
   dateRangeStringToDisplay.value = dateRangeString.value
}

watch(dateRange, () => {
   if (dateRange.value !== null) {
      updateDateRangeString()
   }
})

const saveDateRange = () => {
   if (!dateRange.value?.from || !dateRange.value?.to) {
      dateRange.value = {
         from: formattedStartDate.value,
         to: formattedEndDate.value,
      }
      return
   }

   // Convert local date picker dates to UTC timestamps for API
   // Parse date strings as local dates and convert to UTC timestamps
   const [startYear, startMonth, startDay] = dateRange.value.from.split('/').map(Number)
   const [endYear, endMonth, endDay] = dateRange.value.to.split('/').map(Number)
   
   // Create dates in local timezone (month is 0-indexed in Date constructor)
   const startDate = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0)
   const endDate = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999)
   
   const startTimestamp = startDate.getTime()
   const endTimestamp = endDate.getTime()

   analyticsStore.updateDateRange(startTimestamp, endTimestamp)
}

const closePopupDatepicker = () => {
   // Close the date picker
   popUpOpen.value = false
}
</script>
